import { useMemo, useEffect, useRef } from 'react';
import { useSplitConfig } from './useSplitConfig';

interface DynamicSplitConfigOptions {
  businessId?: string | number;
  userId?: string | number;
  userSettings?: {
    role?: string;
    accountType?: string;
  };
}

/**
 * Hook that provides dynamic Split.io configuration with the ability to update
 * when business ID changes (e.g., when super admin switches businesses)
 */
export const useDynamicSplitConfig = ({ 
  businessId, 
  userId, 
  userSettings 
}: DynamicSplitConfigOptions = {}) => {
  const previousBusinessId = useRef<string | number | undefined>(businessId);
  const configKey = useRef(0);
  
  // Generate Split.io configuration using the existing hook
  const splitConfig = useSplitConfig({
    businessId,
    userId,
    userSettings,
  });

  // Track when business ID changes to force re-initialization
  useEffect(() => {
    if (previousBusinessId.current !== businessId && businessId !== undefined) {
      console.log('[Split.io Dynamic Config] Business ID changed from', previousBusinessId.current, 'to', businessId);
      console.log('[Split.io Dynamic Config] Incrementing config key to force reinitialization');
      configKey.current += 1;
      previousBusinessId.current = businessId;
    }
  }, [businessId]);

  // Return configuration with a key that changes when business ID changes
  // This will force SplitFactoryProvider to reinitialize
  return useMemo(() => ({
    ...splitConfig,
    // Add a unique key that changes when business switches
    __configKey: configKey.current,
  }), [splitConfig, configKey.current]);
};
